import { getPath, isRouteActionType } from '@/router/utils';
import { isMigratedRoute } from '@simplywallst/next-migrate';
import type { FlagName, Flags } from '@simplywallst/unleash';
import type { Middleware } from 'redux';

const FORCE_PROXY_PARAM = '_pr';

const getFlags = () => {
  try {
    // Check if we're in a browser environment before accessing window
    if (typeof window === 'undefined') {
      return undefined;
    }
    const flags = window.localStorage.getItem('unleash:repository:repo');
    if (!flags) {
      return undefined;
    }
    return JSON.parse(flags) as Flags;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

const unleashClient = (flags: Flags) => {
  const isEnabled = (flag: FlagName) => {
    return flags.find((f) => f.name === flag)?.enabled ?? false;
  };
  return { isEnabled };
};

export const migrateMiddleware: Middleware = (_) => (next) => (action) => {
  try {
    if (isRouteActionType(action.type)) {
      const pathname = getPath(action);
      const flags = getFlags();

      if (flags === undefined) {
        return next(action);
      }

      const unleash = unleashClient(flags);
      const forceProxy = action.meta?.query?.[FORCE_PROXY_PARAM];

      // Intercept migrated routes and do a hard navigation to the new url.
      // This causes the route to be handled by the Next.js proxy server.
      if (
        isMigratedRoute(
          pathname,
          // If we've forced the proxy to be web, we don't need to check the flags.
          // This is used to prevent redirect loops when being passed between apps.
          // We still need to check for Next.js specific routes, as they are needed for the app to work.
          forceProxy === 'web' ? () => false : unleash.isEnabled
        ) &&
        typeof window !== 'undefined'
      ) {
        const url = new URL(pathname, window.location.origin);
        url.searchParams.set(FORCE_PROXY_PARAM, 'next');
        window.location.href = url.toString();
        return;
      }
      return next(action);
    }
    return next(action);
  } catch (error) {
    // If this somehow fails, let it silently error.
    // Let the action continue to the next middleware so as to not block the app.
    console.error(error);
  }
  return next(action);
};
